import React, { useState, useEffect, useLayoutEffect } from "react";
import Modal from "components/UI/Modal/Modal";
import { InsertModal, UpdateModal, DeleteModal } from "./Actions";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { HeaderButton } from "components";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Buttons } from "components";
import { ACTIONS } from "consts/Actions";
import { feriadosTitles as titles } from "consts/titulos-de-tablas";
import { labelDeTipoFeriado } from "consts/tipos-feriado";
import styles from "styles/pages/Pages.module.scss";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import XGrid from "components/UI/Grid/XGrid";
import { RNHFeriadoGet } from "services/services-mia/programa-salud/feriados/RNHFeriadoGet";

/**
 * ABM de feriados.
 *
 * Un feriado es el CONCEPTO —"Día del Trabajador", nacional— y no tiene fecha: las
 * fechas son registros aparte, una por año o por día, y se administran en la vista de
 * Series de Feriados. Por eso acá no hay nada de calendario.
 */
export default function Feriados() {
  const credentials = useRecoilValue(userCredentials);
  const setRoute = useSetRecoilState(routeInfo);
  const [modalDetails, setModalDetails] = useState({ isOpen: false });
  const [loading, setLoading] = useState(false);
  const [feriados, setFeriados] = useState([]);
  const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
  const { service } = useService();

  const getFeriados = async () => {
    setFeriados([]);
    setRowSelected({ status: false, row: {} });
    setLoading(true);

    const respuesta = await RNHFeriadoGet(credentials);
    setLoading(false);
    if (!respuesta.ok) return;

    const newRows = respuesta.data
      // Filtra la fila centinela que el back manda cuando no hay registros.
      .filter((data) => data?.rnhFeriadoId)
      .map((data) => ({
        id: data.rnhFeriadoId,
        nombre: data.rnhFeriadoNombre ?? "",
        // La columna muestra la ETIQUETA; el código crudo viaja aparte porque es lo
        // que precarga el select del modal de modificar.
        tipo: labelDeTipoFeriado(data.rnhFeriadoTipo),
        tipoCod: data.rnhFeriadoTipo ?? "",
        // No es columna: lo necesitan los modales de modificar y eliminar.
        version: data.rnhFeriadoVersion,
      }));

    setFeriados(newRows);
  }

  const closeModal = () => {
    setModalDetails({ isOpen: false });
  };
  const handleClick = ({ procede, title }) => {
    setModalDetails({ procede, title, isOpen: true });
  };

  useEffect(() => {
    getFeriados()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    setRoute({
      wordKey: `Nombres de Feriados - Programa Salud RN`
    })
  }, [service.servicioNombre, setRoute])

  return (
    <main className={styles.main}>
      <Modal
        size={modalDetails.procede === ACTIONS.DELETE ? 'sm' : 'md'}
        open={modalDetails.isOpen}
        title={modalDetails.title}
        onClose={closeModal}
      >
        {modalDetails.procede === ACTIONS.UPDATE && (
          <UpdateModal
            row={rowSelected.row}
            updateList={getFeriados}
            closeModal={closeModal}
          />
        )}
        {modalDetails.procede === ACTIONS.DELETE && (
          <DeleteModal
            row={rowSelected.row}
            updateList={getFeriados}
            closeModal={closeModal}
            setterRowSelected={setRowSelected}
          />
        )}
        {modalDetails.procede === ACTIONS.INSERT && (
          <InsertModal
            updateList={getFeriados}
            closeModal={closeModal}
          />
        )}
      </Modal>
      <div className={styles["filters-actions-right"]}>
        <Buttons>
          <HeaderButton
            text={"Actualizar"}
            onClick={() => { getFeriados() }}
            disabled={rowSelected.row.id || false}
          >
            <Cached />
          </HeaderButton>
          <HeaderButton
            text={"Nuevo Feriado"}
            handleClick={handleClick}
            data={{
              title: "Nuevo Feriado",
              procede: ACTIONS.INSERT
            }}
            disabled={!puedeEditarServicio(service) || rowSelected.row.id}
          >
            <Add />
          </HeaderButton>
          <HeaderButton
            text={"Modificar Feriado"}
            handleClick={handleClick}
            data={{
              title: "Modificar Feriado",
              procede: ACTIONS.UPDATE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <EditOutlined />
          </HeaderButton>
          <HeaderButton
            text={"Eliminar Feriado"}
            handleClick={handleClick}
            data={{
              title: "Eliminar Feriado",
              procede: ACTIONS.DELETE
            }}
            disabled={!puedeEditarServicio(service) || (!rowSelected.row.id)}
          >
            <DeleteOutlineRoundedIcon />
          </HeaderButton>
        </Buttons>
      </div>
      <div className={styles.container}>
        <XGrid
          rows={feriados}
          titles={titles}
          loading={loading}
          secondarySelected={rowSelected}
          setSecondarySelected={setRowSelected}
        />
      </div>
    </main>
  );
}
